import React, { useCallback, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { MoveLeft, MoveRight, CircleCheckBig, CheckIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useOnboardingStatus } from '@/contexts/OnboardingStatusContext';

interface OnboardingLayoutProps {
  children: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  hideSteps?: boolean;
  hideNavigation?: boolean;
  onNext?: () => Promise<boolean>; // Function to handle save and continue
  isSaving?: boolean;
  isLoading?: boolean;
  saveButtonText?: string;
  onStepComplete?: (step: number) => void; // Callback when a step is completed
  completedSteps?: number[]; // External list of completed steps to force completion
}

const stepTitles = [
  'Company Profile Setup',
  'Add Team Members',
  'Company Configuration',
  'Your Elements',
  'Review & Finish',
];

export default function OnboardingLayout({
  children,
  currentStep,
  totalSteps,
  hideSteps = false,
  hideNavigation = false,
  onNext,
  isSaving = false,
  isLoading = false,
  saveButtonText = 'Save & Continue',
  onStepComplete,
  completedSteps = [],
}: OnboardingLayoutProps) {
  const navigate = useNavigate();

  // Use centralized onboarding status context - SINGLE SOURCE OF TRUTH
  const {
    isLoading: isLoadingStatus,
    overallProgress,
    isStepCompleted,
    markStepCompleted,
    organizationId
  } = useOnboardingStatus();

  // Handle externally provided completed steps
  useEffect(() => {
    if (completedSteps.length > 0) {
      completedSteps.forEach((step) => markStepCompleted(step));
    }
  }, [completedSteps, markStepCompleted]);

  // Ensure steps before current step are marked as completed
  useEffect(() => {
    for (let i = 1; i < currentStep; i++) {
      if (!isStepCompleted(i)) {
        markStepCompleted(i);
      }
    }
  }, [currentStep, isStepCompleted, markStepCompleted]);

  // Function to mark a step as completed - using context
  const markStepAsCompleted = useCallback(
    (step: number) => {
      markStepCompleted(step);
      if (onStepComplete) {
        onStepComplete(step);
      }
    },
    [onStepComplete, markStepCompleted]
  );

  // Remove the problematic redirect logic entirely
  // Let the step components handle their own navigation after successful save

  // Navigation handlers
  const handleBack = useCallback(() => {
    const stepRoutes = [
      'profile',
      'teamMembers',
      'configuration',
      'elements',
      'complete',
    ];
    const previousStepRoute = stepRoutes[currentStep - 2];

    if (previousStepRoute && organizationId) {
      navigate(`/onboarding/${previousStepRoute}?orgId=${organizationId}`);
    }
  }, [currentStep, navigate, organizationId]);

  const handleNext = useCallback(async () => {
    if (onNext) {
      try {
        // onNext should handle form validation AND API call
        // Only after successful validation + API call should we proceed
        const isCompleted = await onNext();
        console.log(`Handling next for step ${currentStep}`, isCompleted);

        if (isCompleted) {
          // Mark current step as completed after successful save
          markStepAsCompleted(currentStep);

          // Navigate to next step after successful save
          const stepRoutes = [
            'profile',
            'teamMembers',
            'configuration',
            'elements',
            'complete',
          ];
          const nextStepRoute = stepRoutes[currentStep];

          if (nextStepRoute && organizationId) {
            navigate(`/onboarding/${nextStepRoute}?orgId=${organizationId}`);
          }
        }
      } catch (error) {
        // Error handling is done in the step component
        console.error('Save failed:', error);
        toast({
          title: 'Onboarding Failed',
          description:
            'There was an error completing your setup. Please try again.',
          variant: 'destructive',
        });
        // Don't mark as completed if save failed
        // Don't navigate if save failed - stay on current step
        return;
      }
    }
  }, [onNext, currentStep, navigate, organizationId, markStepAsCompleted]);

  // Use progress from context - SINGLE SOURCE OF TRUTH
  const progressPercentage = overallProgress;

  const getStepStatus = (stepNumber: number) => {
    // Use context to check completion status
    if (isStepCompleted(stepNumber)) {
      return 'Completed';
    }

    if (stepNumber === currentStep) {
      return 'In Progress';
    }

    if (stepNumber < currentStep) {
      return 'Completed';
    }

    return 'Pending';
  };

  const getStepCompletionStatus = (stepNumber: number) => {
    // Use context to check completion status
    return isStepCompleted(stepNumber) || stepNumber < currentStep;
  };

  // Show loading state while checking onboarding status
  if (isLoadingStatus) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-12">
      <div className="w-full max-w-7xl">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="mb-6">
            <img
              src="/logo.png"
              alt="LarkPM Logo"
              className="h-12 w-auto object-contain mx-auto"
            />
          </div>

          {/* Title and Subtitle */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-primary mb-2">
              Welcome to LarkPM
            </h1>
            <p className="text-muted-foreground text-lg">
              Let's get your company set up. This will only take a few minutes.
            </p>
          </div>
        </div>{' '}
        {/* Main Form Card */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Header Section - Only show if not hiding steps */}
            {!hideSteps && (
              <div className="px-8 pt-8">
                {/* Step Title and Progress */}
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold tracking-tight text-foreground">
                    Step {currentStep} of {totalSteps}:{' '}
                    {stepTitles[currentStep - 1]}
                  </h2>
                  <span className="text-md font-medium text-muted-foreground">
                    {Math.round(progressPercentage)}% Complete
                  </span>
                </div>{' '}
                {/* Stepper and Progress Bar Container */}
                <div className="flex flex-col gap-6">
                  {/* Progress Bar */}
                  <div className="flex-1">
                    <Progress value={progressPercentage} className="h-2" />
                  </div>

                  {/* Step Indicator - Left side on desktop, top on mobile */}
                  <div className="flex justify-between items-center lg:gap-6 flex-shrink-0">
                    {stepTitles.map((title, index) => {
                      const stepNumber = index + 1;
                      const isCompleted = getStepCompletionStatus(stepNumber);
                      const isCurrent = stepNumber === currentStep;
                      const status = getStepStatus(stepNumber);

                      return (
                        <div
                          key={stepNumber}
                          className="flex items-center gap-3"
                        >
                          {/* Step Circle */}
                          <div
                            className={`
                              flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                              ${isCompleted
                                ? 'bg-yellow-500 text-primary-foreground'
                                : isCurrent
                                  ? 'bg-foreground-soft text-primary-foreground'
                                  : 'bg-muted text-muted-foreground'
                              }
                            `}
                          >
                            {isCompleted ? (
                              <CheckIcon className="w-5 h-5 text-primary" />
                            ) : (
                              stepNumber
                            )}
                          </div>
                          {/* Step Details - Hidden on small screens */}
                          <div className="hidden sm:block">
                            {/* Step Title */}
                            <div
                              className={`
                                text-sm font-medium
                                ${isCurrent ? 'text-foreground-soft' : 'text-muted-foreground'}
                              `}
                            >
                              {title}
                            </div>
                            {/* Step Status */}
                            <div
                              className={`
                                text-xs font-medium
                                ${isCompleted
                                  ? 'text-yellow-600'
                                  : isCurrent
                                    ? 'text-foreground-soft'
                                    : 'text-muted-foreground/70'
                                }
                              `}
                            >
                              {status}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Form Content */}
            <div className="p-8">
              {children}

              {/* Centralized Navigation Buttons */}
              {!hideNavigation && (
                <div className="flex justify-between pt-6 mt-6 border-t border-border">
                  <Button
                    type="button"
                    onClick={handleBack}
                    variant="outline"
                    disabled={currentStep === 1 || isSaving || isLoading}
                    className="border-border flex items-center gap-2 disabled:opacity-50"
                  >
                    <MoveLeft className="w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={isSaving || isLoading}
                    className={
                      currentStep === totalSteps
                        ? 'bg-yellow-500 hover:bg-yellow-600 text-primary flex items-center gap-2 disabled:opacity-50'
                        : 'bg-foreground-soft hover:bg-foreground text-primary-foreground flex items-center gap-2 disabled:opacity-50'
                    }
                  >
                    {isSaving || isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {isLoading ? 'Saving...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        {currentStep === totalSteps ? (
                          <>
                            <CircleCheckBig className="w-4 h-4" />
                            Finish Setup
                          </>
                        ) : (
                          <>
                            {saveButtonText}
                            <MoveRight className="w-4 h-4" />
                          </>
                        )}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
